import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  output,
} from '@angular/core';
import {
  CdkDrag,
  CdkDragEnd,
  CdkDragMove,
  CdkDragStart,
} from '@angular/cdk/drag-drop';
import { WindowContentService } from '@core/components/sidebar/components/flowpilot/window/services/window-content.service';

export interface Position {
  left: number;
  top: number;
}

export interface BasePosition extends Position {
  startX: number;
  startY: number;
}

export interface Bounds extends Position {
  w: number;
  h: number;
}

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

@Component({
  selector: 'app-flowpilot-window-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkDrag],
  host: { class: 'block size-full' },
  template: `
    <div class="flex h-full w-full flex-col">
      <div class="flex h-min w-full">
        <div
          cdkDrag
          class="size-2"
          [class.cursor-nwse-resize]="!onDiagonalEdge()"
          [class.cursor-nw-resize]="onDiagonalEdge()"
          (cdkDragStarted)="onResizeStarted()"
          (cdkDragMoved)="onResizeMoved($event, 'nw')"
          (cdkDragEnded)="onResizeMoved($event, 'nw')"></div>

        <div
          cdkDrag
          class="h-2 w-full"
          [class.cursor-ns-resize]="!onVerticalEdge()"
          [class.cursor-n-resize]="onVerticalEdge()"
          (cdkDragStarted)="onResizeStarted()"
          (cdkDragMoved)="onResizeMoved($event, 'n')"
          (cdkDragEnded)="onResizeMoved($event, 'n')"></div>

        <div
          cdkDrag
          class="size-2"
          [class.cursor-nesw-resize]="!onDiagonalEdge()"
          [class.cursor-ne-resize]="onDiagonalEdge()"
          (cdkDragStarted)="onResizeStarted()"
          (cdkDragMoved)="onResizeMoved($event, 'ne')"
          (cdkDragEnded)="onResizeMoved($event, 'ne')"></div>
      </div>

      <div class="flex h-full min-h-0 w-full min-w-0">
        <div
          cdkDrag
          class="h-full w-2"
          [class.cursor-ew-resize]="!onHorizontalEdge()"
          [class.cursor-w-resize]="onHorizontalEdge()"
          (cdkDragStarted)="onResizeStarted()"
          (cdkDragMoved)="onResizeMoved($event, 'w')"
          (cdkDragEnded)="onResizeMoved($event, 'w')"></div>

        <div class="flex h-full w-full min-w-0 flex-col gap-y-1.5">
          <div
            cdkDrag
            class="mt-0.5 h-min w-full cursor-move px-1.5"
            (cdkDragStarted)="onWindowMoveStarted($event)"
            (cdkDragMoved)="onWindowMoved($event)"
            (cdkDragEnded)="onWindowMoveEnded($event)">
            <ng-content select="[header]" />
          </div>

          <div
            class="flex h-full min-h-0 w-full min-w-0 overflow-hidden rounded-xl">
            <ng-content select="[content]" />
          </div>
        </div>

        <div
          cdkDrag
          class="h-full w-2"
          [class.cursor-ew-resize]="!onHorizontalEdge()"
          [class.cursor-e-resize]="onHorizontalEdge()"
          (cdkDragStarted)="onResizeStarted()"
          (cdkDragMoved)="onResizeMoved($event, 'e')"
          (cdkDragEnded)="onResizeMoved($event, 'e')"></div>
      </div>

      <div class="flex h-min w-full items-end">
        <div
          cdkDrag
          class="size-2"
          [class.cursor-nesw-resize]="!onDiagonalEdge()"
          [class.cursor-sw-resize]="onDiagonalEdge()"
          (cdkDragStarted)="onResizeStarted()"
          (cdkDragMoved)="onResizeMoved($event, 'sw')"
          (cdkDragEnded)="onResizeMoved($event, 'sw')"></div>

        <div
          cdkDrag
          class="h-2 w-full cursor-ns-resize"
          [class.cursor-nesw-resize]="!onVerticalEdge()"
          [class.cursor-s-resize]="onVerticalEdge()"
          (cdkDragStarted)="onResizeStarted()"
          (cdkDragMoved)="onResizeMoved($event, 's')"
          (cdkDragEnded)="onResizeMoved($event, 's')"></div>

        <div
          cdkDrag
          class="size-2"
          [class.cursor-nwse-resize]="!onDiagonalEdge()"
          [class.cursor-se-resize]="onDiagonalEdge()"
          (cdkDragStarted)="onResizeStarted()"
          (cdkDragMoved)="onResizeMoved($event, 'se')"
          (cdkDragEnded)="onResizeMoved($event, 'se')"></div>
      </div>
    </div>
  `,
})
export class FlowpilotWindowLayoutComponent {
  /** INJECTIONS **/
  /****/
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

  /****/
  private readonly windowService = inject(WindowContentService);

  /** OUTPUTS **/
  /****/
  readonly windowMoved = output<Position>();

  /****/
  readonly resizeMoved = output<Bounds>();

  /** PROPERTIES **/
  /****/
  protected readonly windowWidth = this.windowService.widthSignal;

  /****/
  protected readonly windowHeight = this.windowService.heightSignal;

  /** Captured at drag start so mousemove can compute absolute position **/
  private _dragBase: BasePosition = { left: 0, top: 0, startX: 0, startY: 0 };

  /** Captured at resize start — full bounds so north/west edges shift position too **/
  private _boundsAtResizeStart: Bounds = { left: 0, top: 0, w: 0, h: 0 };

  /****/
  protected readonly MIN_WIDTH = 400;

  /****/
  protected readonly MIN_HEIGHT = 400;

  /****/
  protected readonly onVerticalEdge = computed(
    () => this.windowHeight() <= this.MIN_HEIGHT
  );

  protected readonly onHorizontalEdge = computed(
    () => this.windowWidth() <= this.MIN_WIDTH
  );

  protected readonly onDiagonalEdge = computed(
    () => this.onVerticalEdge() && this.onHorizontalEdge()
  );

  protected onWindowMoveStarted(event: CdkDragStart) {
    const rect = this.el.nativeElement.getBoundingClientRect();
    const e = event.event as MouseEvent;

    const left = rect.left;
    const top = rect.top;
    const startX = e.clientX;
    const startY = e.clientY;

    this._dragBase = { left, top, startX, startY };
  }

  protected onWindowMoved(event: CdkDragMove) {
    this.emitMoveEvent(event.pointerPosition.x, event.pointerPosition.y);
    event.source.reset();
  }

  protected onWindowMoveEnded(event: CdkDragEnd) {
    this.emitMoveEvent(event.dropPoint.x, event.dropPoint.y);
    event.source.reset();
  }

  private emitMoveEvent(x: number, y: number) {
    const startX = this._dragBase.startX ?? 0;
    const startY = this._dragBase.startY ?? 0;

    const left = this._dragBase.left + (x - startX);
    const top = this._dragBase.top + (y - startY);

    this.windowMoved.emit({ left, top });
  }

  protected onResizeStarted(): void {
    const rect = this.el.nativeElement.getBoundingClientRect();

    const left = rect.left;
    const top = rect.top;
    const w = rect.width;
    const h = rect.height;

    this._boundsAtResizeStart = { left, top, w, h };
  }

  protected onResizeMoved(event: CdkDragMove | CdkDragEnd, edge: ResizeEdge) {
    const dX = event.distance.x;
    const dY = event.distance.y;
    this.resizeMoved.emit(this.calculateResizeBounds(edge, dX, dY));
    event.source.reset();
  }

  private calculateResizeBounds(edge: ResizeEdge, dX: number, dY: number) {
    let { left, top, w, h } = this._boundsAtResizeStart;

    if (edge.includes('e')) w = this.resizeEast(w, dX);
    else if (edge.includes('w')) {
      const temp = this.resizeWest(left, w, dX);
      left = temp.left;
      w = temp.w;
    }

    if (edge.includes('s')) h = this.resizeSouth(h, dY);
    else if (edge.includes('n')) {
      const temp = this.resizeNorth(top, h, dY);
      top = temp.top;
      h = temp.h;
    }

    return { left, top, w, h } as Bounds;
  }

  private resizeEast(oldWidth: number, dX: number) {
    return Math.max(this.MIN_WIDTH, oldWidth + dX);
  }

  private resizeSouth(oldHeight: number, dY: number) {
    return Math.max(this.MIN_HEIGHT, oldHeight + dY);
  }

  private resizeWest(oldLeft: number, oldWidth: number, dX: number) {
    const newW = Math.max(this.MIN_WIDTH, oldWidth - dX);
    const newLeft = oldLeft + oldWidth - newW;

    return { left: newLeft, w: newW };
  }

  private resizeNorth(oldTop: number, oldHeight: number, dY: number) {
    const newH = Math.max(this.MIN_HEIGHT, oldHeight - dY);
    const newTop = oldTop + oldHeight - newH;

    return { top: newTop, h: newH };
  }
}
